import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Box } from '@mui/material';
import { Domain, Hooks } from '@3nickels/data-modules';
import { useMessage } from '../../../hooks/useMessage';
import { useLoading } from '../../../hooks/useLoading';
import Modal from '../../../components/Modal';
import { getName, formatPrice, getInterval } from './functions';
import { handleResubscribe } from './utils';
import { useDefaultPaymentMethod } from '../../../hooks/payment/useDefaultPaymentMethod';
import { usePaymentMethods } from '../../../hooks/payment/usePaymentMethods';
import { formatDateMMDDYYYY } from '../../../helpers/utilityFunctions';

interface ResubscribeModalProps {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  subscription: Domain.SubscriptionInfo;
  threeNickelsProducts: Domain.PricebookBundle[] | undefined;
  onResubscribe?: () => void;
}

const ResubscribeModal: React.FC<ResubscribeModalProps> = ({
  modalOpen,
  setModalOpen,
  subscription,
  threeNickelsProducts,
  onResubscribe,
}) => {
  const { t } = useTranslation();
  const commands = Hooks.useCommands();
  const { showMessage } = useMessage();
  const { setLoading } = useLoading();

  const pricebookName = getName(subscription, threeNickelsProducts);
  const defaultPaymentMethodId = useDefaultPaymentMethod();
  const paymentMethods = usePaymentMethods();
  const defaultPaymentMethod = paymentMethods?.find(
    (method) => method.id === defaultPaymentMethodId
  );
  const price = formatPrice(subscription.price);
  const interval = getInterval(subscription.interval);
  const subscriptionStatus = subscription.mappedStatus;
  const isExpired = subscriptionStatus?.expired;

  const handleResubscribeClick = async () => {
    setModalOpen(false);
    onResubscribe?.();
    await handleResubscribe(commands, subscription.pricebookId, setLoading, showMessage);
  };

  const modalTitle = t('RenewSubscriptionToPricebook', { pricebookName });

  const modalContent = (
    <>
      <Typography variant='p16' color='secondary' mb={2}>
        {isExpired
          ? t('YourAccessWillResume', { pricebookName })
          : t('YouWillKeepYourAccess', { pricebookName })}
      </Typography>

      <Typography variant='p16' color='secondary' mb={3}>
        {isExpired
          ? t('YouWillBeChargedImmediately', {
              price,
              interval,
            })
          : t('YourNextPaymentWillBeOn', {
              date: formatDateMMDDYYYY(subscription.expirationDate),
              interval,
            })}
      </Typography>

      <Typography variant='p16Bold' color='secondary' mb={0.5}>
        {price}/{interval}
      </Typography>

      {defaultPaymentMethod ? (
        <Box>
          <Typography variant='p16Bold' color='secondary'>
            {t('PaymentMethodEndingInX', {
              paymentMethod: 'Card',
              x: defaultPaymentMethod.lastDigits,
            })}
          </Typography>
        </Box>
      ) : (
        <Typography variant='p16' color='secondary'>
          {t('NoPaymentMethodOnFile')}
        </Typography>
      )}
    </>
  );

  return (
    <Modal
      title={modalTitle}
      primaryButtonText='Resubscribe'
      secondaryButtonText='Cancel'
      open={modalOpen}
      setOpen={setModalOpen}
      handleSave={handleResubscribeClick}>
      {modalContent}
    </Modal>
  );
};

export default ResubscribeModal;
